import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Check, Loader2, LogIn, Crown } from "lucide-react";

const PERKS = [
  "Unlimited chats with home owners",
  "See full owner contact details",
  "Unlimited swipes, every day",
  "Priority support",
];

export default function Premium() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user) api.get("/me/subscription").then((r) => setSub(r.data)); }, [user]);

  const subscribe = async () => {
    setBusy(true);
    try {
      const r = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch { setBusy(false); }
  };

  const trialDays = sub?.trial_ends_at ? Math.max(0, Math.ceil((new Date(sub.trial_ends_at) - new Date()) / 86400000)) : 0;

  return (
    <div className="min-h-[88vh] bg-gradient-to-b from-[#ff6b5b] to-[#ff8a5b] px-6 py-12" data-testid="premium-page">
      <div className="max-w-md mx-auto text-center text-white">
        <div className="h-16 w-16 rounded-3xl bg-white/20 backdrop-blur flex items-center justify-center mx-auto"><Crown className="h-8 w-8" /></div>
        <h1 className="mt-5 font-display text-4xl font-extrabold">VYAPAAR Premium</h1>
        <p className="mt-2 text-white/90">Keep the conversations going. First month free, then just <b>₹199/month</b>.</p>

        {sub?.status === "premium" && <p className="mt-4 bg-white/20 rounded-full py-2 px-4 inline-block font-semibold">✓ You're Premium — thank you!</p>}
        {sub?.status === "trial" && <p className="mt-4 bg-white/20 rounded-full py-2 px-4 inline-block font-semibold">🎁 Free trial active — {trialDays} day{trialDays !== 1 ? "s" : ""} left</p>}
      </div>

      <div className="max-w-md mx-auto mt-8 bg-white rounded-[28px] p-7 shadow-2xl">
        <div className="flex items-end gap-1"><span className="font-display text-5xl font-extrabold text-slate-900">₹199</span><span className="text-slate-400 mb-2">/month</span></div>
        <p className="text-[#ff6b5b] font-semibold mt-1">First month free ✨</p>
        <ul className="mt-5 space-y-3">
          {PERKS.map((p) => (
            <li key={p} className="flex items-center gap-3 text-slate-700"><span className="h-6 w-6 rounded-full bg-[#fff0eb] text-[#ff6b5b] flex items-center justify-center shrink-0"><Check className="h-4 w-4" /></span>{p}</li>
          ))}
        </ul>
        {!user ? (
          <Button data-testid="premium-login" onClick={login} className="mt-7 w-full h-12 rounded-full bg-[#ff6b5b] hover:bg-[#f2523f] text-white text-base"><LogIn className="h-4 w-4 mr-2" />Sign in to subscribe</Button>
        ) : sub?.status === "premium" ? (
          <Button onClick={() => navigate("/chats")} className="mt-7 w-full h-12 rounded-full bg-slate-900 text-white">Go to your chats</Button>
        ) : (
          <Button data-testid="premium-subscribe" onClick={subscribe} disabled={busy} className="mt-7 w-full h-12 rounded-full bg-[#ff6b5b] hover:bg-[#f2523f] text-white text-base">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : (sub?.status === "trial" ? "Subscribe now — ₹199/mo" : "Start Premium")}
          </Button>
        )}
        <p className="text-center text-xs text-slate-400 mt-3">Cancel anytime. Posting property is always free.</p>
      </div>
    </div>
  );
}
